import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi, auditApi, type UserProfile, type AuditEntry } from "@/integrations/mysql/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users, History, Plus, Trash2, Shield, ShieldOff,
  ChevronLeft, ChevronRight, Search, X, RefreshCw,
} from "lucide-react";

// ── Shared helpers ─────────────────────────────────────────────────────────────

function Tab({ id, active, onClick, icon: Icon, label }: {
  id: string; active: boolean; onClick: (id: string) => void;
  icon: React.ElementType; label: string;
}) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? "bg-brand-primary text-white" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function Badge({ role }: { role: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
      role === 'admin'
        ? 'bg-brand-primary/10 text-brand-primary'
        : 'bg-gray-100 text-gray-500'
    }`}>
      {role === 'admin' ? <Shield className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
      {role === 'admin' ? 'Admin' : 'Paciente'}
    </span>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Resource label map ─────────────────────────────────────────────────────────

const RESOURCE_LABELS: Record<string, string> = {
  page_content: 'Conteúdo de página',
  settings:     'Configuração',
  user:         'Usuário',
  testimonial:  'Depoimento',
  faq:          'FAQ',
  blog_post:    'Post do blog',
  service:      'Serviço',
};

const PAGE_LABELS: Record<string, string> = {
  home:         'Início',
  contact:      'Contato',
  blog_page:    'Blog',
  nossa_historia: 'Quem Somos',
};

// ── Add User Modal ──────────────────────────────────────────────────────────────

function AddUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'patient' as 'admin' | 'patient' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const { toast } = useToast();

  const handleSave = async () => {
    if (!form.email || !form.password) { setErr('Email e senha são obrigatórios.'); return; }
    if (form.password.length < 6) { setErr('Senha deve ter pelo menos 6 caracteres.'); return; }
    setSaving(true); setErr('');
    try {
      await usersApi.create(form);
      toast({ title: 'Usuário criado com sucesso!' });
      onCreated();
      onClose();
    } catch (e: any) {
      setErr(e.message || 'Erro ao criar usuário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-800">Novo Usuário</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {err && <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Nome</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Nome completo" maxLength={100}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email*</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="email@exemplo.com" maxLength={255}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Senha*</label>
            <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              placeholder="Mínimo 6 caracteres"
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Permissão</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value as any }))}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
              <option value="patient">Paciente</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
            {saving ? 'Salvando...' : 'Criar usuário'}
          </button>
          <button onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users-all'],
    queryFn:  () => usersApi.all(),
  });

  const roleToggle = useMutation({
    mutationFn: ({ id, role }: { id: string; role: 'admin' | 'patient' }) => usersApi.updateRole(id, role),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users-all'] }); toast({ title: 'Permissão alterada!' }); },
    onError:   (e: any) => toast({ title: e.message, variant: 'destructive' }),
  });

  const del = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users-all'] }); toast({ title: 'Usuário removido.' }); },
    onError:   (e: any) => toast({ title: e.message, variant: 'destructive' }),
  });

  if (isLoading) return <div className="py-10 text-center text-sm text-gray-400">Carregando...</div>;

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-gray-400">{users.length} usuário{users.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
          <Plus className="w-3.5 h-3.5" /> Novo usuário
        </button>
      </div>

      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
            <div className="w-9 h-9 rounded-xl bg-brand-primary flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">
                {(u.name || u.email).slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{u.name || <span className="italic text-gray-400">Sem nome</span>}</p>
              <p className="text-xs text-gray-400 truncate">{u.email}</p>
            </div>
            <Badge role={u.role} />
            <p className="text-xs text-gray-400 hidden sm:block shrink-0">
              {u.created_at ? fmtDate(u.created_at) : '—'}
            </p>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => roleToggle.mutate({ id: u.id, role: u.role === 'admin' ? 'patient' : 'admin' })}
                disabled={u.id === profile?.id}
                title={u.role === 'admin' ? 'Remover admin' : 'Tornar admin'}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-brand-primary hover:border-brand-primary/30 transition-colors disabled:opacity-30"
              >
                {u.role === 'admin' ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setDeleteId(u.id)}
                disabled={u.id === profile?.id}
                title="Excluir usuário"
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors disabled:opacity-30"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showAdd && (
        <AddUserModal onClose={() => setShowAdd(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['users-all'] })} />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. O usuário perderá o acesso imediatamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { del.mutate(deleteId); setDeleteId(null); } }} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Audit tab ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

function AuditTab() {
  const { toast } = useToast();
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [filterResource, setFilterResource] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit', offset, filterResource, search],
    queryFn: () => auditApi.list({
      limit:      PAGE_SIZE,
      offset,
      resource:   filterResource || undefined,
      user_email: search || undefined,
    }),
  });

  const total   = data?.total ?? 0;
  const entries = data?.entries ?? [];
  const pages   = Math.ceil(total / PAGE_SIZE);
  const current = Math.floor(offset / PAGE_SIZE) + 1;

  const ACTION_COLOR: Record<string, string> = {
    'criou conteúdo':     'bg-emerald-100 text-emerald-700',
    'atualizou conteúdo': 'bg-sky-100 text-sky-700',
    'excluiu conteúdo':   'bg-red-100 text-red-600',
    'atualizou configuração': 'bg-amber-100 text-amber-700',
    'criou usuário':      'bg-violet-100 text-violet-700',
    'excluiu usuário':    'bg-red-100 text-red-600',
    'alterou permissão':  'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }}
            placeholder="Filtrar por e-mail..." maxLength={100}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
        </div>
        <select value={filterResource} onChange={e => { setFilterResource(e.target.value); setOffset(0); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
          <option value="">Todos os recursos</option>
          {Object.entries(RESOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => refetch()} title="Atualizar"
          className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:text-brand-primary hover:border-brand-primary/30 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-sm text-gray-400">Carregando histórico...</div>
      ) : entries.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-400">Nenhum registro encontrado.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Data / Hora</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Usuário</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ação</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Recurso</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Detalhe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map((e: AuditEntry) => (
                  <tr key={e.id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-[150px]">
                      <span className="block truncate">{e.user_name ?? e.user_email ?? '—'}</span>
                      {e.user_name && e.user_email && (
                        <span className="block truncate text-gray-400">{e.user_email}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${ACTION_COLOR[e.action] ?? 'bg-gray-100 text-gray-600'}`}>
                        {e.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">
                      {RESOURCE_LABELS[e.resource] ?? e.resource}
                      {e.page && <span className="ml-1 text-gray-400">· {PAGE_LABELS[e.page] ?? e.page}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 hidden md:table-cell max-w-[200px] truncate">
                      {e.resource_label ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">{total} registro{total !== 1 ? 's' : ''} · página {current} de {pages}</p>
              <div className="flex gap-1">
                <button onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-brand-primary disabled:opacity-30 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setOffset(o => o + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-brand-primary disabled:opacity-30 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export function UsuariosManager() {
  const [tab, setTab] = useState<'users' | 'audit'>('users');

  return (
    <div className="space-y-5">
      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <Tab id="users" active={tab === 'users'} onClick={t => setTab(t as any)} icon={Users} label="Usuários" />
        <Tab id="audit" active={tab === 'audit'} onClick={t => setTab(t as any)} icon={History} label="Histórico de Alterações" />
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}
